import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
    // eslint-disable-next-line no-console
    console.warn("REACT_APP_BACKEND_URL missing. API calls will fail.");
}

export const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    timeout: 15000,
    headers: { "Content-Type": "application/json" },
});

export async function submitSurvey(payload) {
    // The submit endpoint is the most consequential POST in the app. If it
    // ever sees a transient 404 (e.g. ingress proxy returning 404 for a few
    // hundred ms during a backend restart), retry once after a short pause
    // before propagating the error to the user.
    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const { data } = await api.post("/survey/submit", payload);
            return { ok: true, data };
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                return { ok: false, rateLimited: true, data: err.response.data };
            }
            if (status === 409) {
                // Per-email cap reached. Surface the email so the caller can
                // route the user to the returning-snapshot lookup.
                return {
                    ok: false,
                    maxAttempts: true,
                    data: err.response.data,
                };
            }
            if (status === 404 && attempt === 0) {
                await new Promise((r) => setTimeout(r, 800));
                continue;
            }
            return {
                ok: false,
                rateLimited: false,
                error:
                    err?.response?.data?.detail ??
                    err?.message ??
                    "Network error",
            };
        }
    }
    return { ok: false, error: "Network error" };
}

export async function getSurveyCount() {
    try {
        const { data } = await api.get("/survey/count");
        return data?.total ?? 0;
    } catch {
        return 0;
    }
}

export async function getGlimpse(payload) {
    const { data } = await api.post("/glimpse", payload);
    return data;
}

export async function getShareStatus(shareCode) {
    try {
        const { data } = await api.get(
            `/share/status/${encodeURIComponent(shareCode)}`,
        );
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            notFound: err?.response?.status === 404,
            error: err?.message ?? "Network error",
        };
    }
}

export async function lookupShareByEmail(email) {
    try {
        const { data } = await api.get("/share/lookup", {
            params: { email },
        });
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            notFound: err?.response?.status === 404,
            rateLimited: err?.response?.status === 429,
            error: err?.message ?? "Network error",
        };
    }
}

export async function getReport({ responseId, code }) {
    try {
        const { data } = await api.get(
            `/report/${encodeURIComponent(responseId)}`,
            { params: { code } },
        );
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            notFound: err?.response?.status === 404,
            forbidden: err?.response?.status === 403,
            error: err?.message ?? "Network error",
        };
    }
}

export async function patchEmailOnResponse({ responseId, email, shareCode, name }) {
    try {
        const body = { share_code: shareCode };
        if (email !== undefined) body.email = email;
        if (name !== undefined) body.name = name;
        const { data } = await api.patch(
            `/survey/${encodeURIComponent(responseId)}/email`,
            body,
        );
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            error:
                err?.response?.data?.detail ?? err?.message ?? "Network error",
        };
    }
}

export async function lookupSnapshotsByEmail(email) {
    try {
        const { data } = await api.post("/survey/by-email", { email });
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            rateLimited: err?.response?.status === 429,
            invalid: err?.response?.status === 422,
            error:
                err?.response?.data?.detail ??
                err?.response?.data?.message ??
                err?.message ??
                "Network error",
        };
    }
}

